import { useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { format, parseISO } from 'date-fns'
import { Thermometer, Droplets, Battery, TrendingUp, TrendingDown, Minus, Sprout, Wind } from 'lucide-react'
import type { Dht11SoilMeasurement } from '@/types/measurement.types'
import type { Device } from '@/types/device.types'
import { useUserUnits, convertTemp, formatTemp, tempLabel } from '@/utils/units'

interface DHT11ViewProps {
  device: Device
  measurements: Dht11SoilMeasurement[]
}

export const DHT11View = ({ measurements }: DHT11ViewProps) => {
  const units = useUserUnits()
  const latestMeasurement = measurements[0]

  const chartData = useMemo(() => {
    return measurements
      .slice()
      .reverse()
      .map((m) => ({
        timestamp: format(parseISO(m.measurementDateUtc), 'MMM dd HH:mm'),
        soilTemp: m.temperature != null ? convertTemp(m.temperature, units.temperature) : null,
        soilMoisture: m.soilmoistureLevel,
        airTemp: m.temperature != null ? convertTemp(m.temperature, units.temperature) : null,
        airHumidity: m.humidity,
        battery: m.batteryVoltage || 0,
      }))
  }, [measurements, units.temperature])

  const stats = useMemo(() => {
    if (measurements.length === 0) return null

    const soilTemps = measurements.map(m => m.temperature ?? 0)
    const soilMoistures = measurements.map(m => m.soilmoistureLevel ?? 0)
    const airTemps = measurements.map(m => m.temperature ?? 0)
    const airHumidities = measurements.map(m => m.humidity ?? 0)

    const calculate = (values: number[]) => ({
      avg: values.reduce((a, b) => a + b, 0) / values.length,
      max: Math.max(...values),
      min: Math.min(...values),
      trend: values.length > 1 ? values[0] - values[1] : 0
    })

    return {
      soilTemp: calculate(soilTemps),
      soilMoisture: calculate(soilMoistures),
      airTemp: calculate(airTemps),
      airHumidity: calculate(airHumidities)
    }
  }, [measurements])

  const getTrendIcon = (trend: number, inverted = false) => {
    const threshold = 0.5
    if (Math.abs(trend) < threshold) return <Minus className="h-4 w-4 text-gray-500" />

    const isUp = trend > threshold
    const isGood = inverted ? !isUp : isUp

    if (isUp) return <TrendingUp className={`h-4 w-4 ${isGood ? 'text-green-500' : 'text-red-500'}`} />
    return <TrendingDown className={`h-4 w-4 ${isGood ? 'text-blue-500' : 'text-orange-500'}`} />
  }

  const getSoilCondition = (moisture?: number, temp?: number) => {
    if (!moisture || !temp) return { status: 'Unknown', color: 'text-gray-600' }

    if (moisture >= 20 && moisture <= 60 && temp >= 15 && temp <= 30) {
      return { status: 'Optimal', color: 'text-green-600' }
    } else if ((moisture >= 10 && moisture <= 70) && (temp >= 10 && temp <= 35)) {
      return { status: 'Acceptable', color: 'text-yellow-600' }
    } else if (moisture < 10) {
      return { status: 'Too Dry', color: 'text-red-600' }
    } else if (moisture > 70) {
      return { status: 'Too Wet', color: 'text-blue-600' }
    } else {
      return { status: 'Poor', color: 'text-orange-600' }
    }
  }

  const soilCondition = getSoilCondition(latestMeasurement?.soilmoistureLevel, latestMeasurement?.temperature)

  return (
    <div className="space-y-6">
      {/* Current Status Cards - Soil */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Sprout className="h-5 w-5" />
          Soil Conditions
        </h3>
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Soil Temperature</CardTitle>
              <Thermometer className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {latestMeasurement?.temperature != null ? formatTemp(latestMeasurement.temperature, units.temperature) : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                {stats && (
                  <>
                    {getTrendIcon(stats.soilTemp.trend)}
                    {stats.soilTemp.trend > 0 ? '+' : ''}{formatTemp(stats.soilTemp.trend, units.temperature)}
                  </>
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Soil Moisture</CardTitle>
              <Droplets className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {latestMeasurement ? `${latestMeasurement.soilmoistureLevel?.toFixed(1)}%` : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                {stats && (
                  <>
                    {getTrendIcon(stats.soilMoisture.trend)}
                    {stats.soilMoisture.trend > 0 ? '+' : ''}{stats.soilMoisture.trend.toFixed(1)}%
                  </>
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Soil Status</CardTitle>
              <Badge variant="outline" className={soilCondition.color}>
                {soilCondition.status}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="text-sm space-y-1">
                <div>Temp: {stats ? `${formatTemp(stats.soilTemp.min, units.temperature)} - ${formatTemp(stats.soilTemp.max, units.temperature)}` : 'N/A'}</div>
                <div>Moist: {stats?.soilMoisture.min.toFixed(0)}% - {stats?.soilMoisture.max.toFixed(0)}%</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Battery</CardTitle>
              <Battery className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {latestMeasurement?.batteryVoltage ? `${latestMeasurement.batteryVoltage.toFixed(2)}V` : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                Last: {latestMeasurement ? format(parseISO(latestMeasurement.measurementDateUtc), 'HH:mm') : ''}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Current Status Cards - Air */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Wind className="h-5 w-5" />
          Air Conditions
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Air Temperature</CardTitle>
              <Thermometer className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {latestMeasurement?.temperature != null ? formatTemp(latestMeasurement.temperature, units.temperature) : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                Range: {stats ? `${formatTemp(stats.airTemp.min, units.temperature)} - ${formatTemp(stats.airTemp.max, units.temperature)}` : 'N/A'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Air Humidity</CardTitle>
              <Droplets className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {latestMeasurement ? `${latestMeasurement.humidity?.toFixed(1)}%` : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                Range: {stats?.airHumidity.min.toFixed(0)}% - {stats?.airHumidity.max.toFixed(0)}%
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Soil Measurements Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Soil Conditions Over Time</CardTitle>
          <CardDescription>
            Temperature and moisture levels in the soil
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  yAxisId="temp"
                  style={{ fontSize: '12px' }}
                  label={{ value: `Temperature (${tempLabel(units.temperature)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  width={60}
                  tickCount={8}
                />
                <YAxis
                  yAxisId="moisture"
                  orientation="right"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Moisture (%)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  domain={[0, 100]}
                  width={60}
                  tickCount={6}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name.includes('Temperature')) return [`${value.toFixed(1)}${tempLabel(units.temperature)}`, name]
                    if (name.includes('Moisture')) return [`${value.toFixed(1)}%`, name]
                    return [value, name]
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="soilTemp"
                  stroke="#d97706"
                  name="Soil Temperature"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="moisture"
                  type="monotone"
                  dataKey="soilMoisture"
                  stroke="#0891b2"
                  name="Soil Moisture"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">
              No measurement data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Air Measurements Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Air Conditions Over Time</CardTitle>
          <CardDescription>
            Ambient temperature and humidity levels
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  yAxisId="temp"
                  style={{ fontSize: '12px' }}
                  label={{ value: `Temperature (${tempLabel(units.temperature)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  width={60}
                  tickCount={8}
                />
                <YAxis
                  yAxisId="humidity"
                  orientation="right"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Humidity (%)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  domain={[0, 100]}
                  width={60}
                  tickCount={6}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name.includes('Temperature')) return [`${value.toFixed(1)}${tempLabel(units.temperature)}`, name]
                    if (name.includes('Humidity')) return [`${value.toFixed(1)}%`, name]
                    return [value, name]
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="airTemp"
                  stroke="#ef4444"
                  name="Air Temperature"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="humidity"
                  type="monotone"
                  dataKey="airHumidity"
                  stroke="#3b82f6"
                  name="Air Humidity"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">
              No measurement data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Summary */}
      <Card>
        <CardHeader>
          <CardTitle>7-Day Statistics</CardTitle>
          <CardDescription>
            Summary of all sensor readings over the past week
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats ? (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Sprout className="h-4 w-4" />
                    Soil Measurements
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Avg Temp</p>
                      <p className="font-medium">{formatTemp(stats.soilTemp.avg, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Min Temp</p>
                      <p className="font-medium">{formatTemp(stats.soilTemp.min, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max Temp</p>
                      <p className="font-medium">{formatTemp(stats.soilTemp.max, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Avg Moisture</p>
                      <p className="font-medium">{stats.soilMoisture.avg.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Min Moisture</p>
                      <p className="font-medium">{stats.soilMoisture.min.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max Moisture</p>
                      <p className="font-medium">{stats.soilMoisture.max.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Wind className="h-4 w-4" />
                    Air Measurements
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Avg Temp</p>
                      <p className="font-medium">{formatTemp(stats.airTemp.avg, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Min Temp</p>
                      <p className="font-medium">{formatTemp(stats.airTemp.min, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max Temp</p>
                      <p className="font-medium">{formatTemp(stats.airTemp.max, units.temperature)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Avg Humidity</p>
                      <p className="font-medium">{stats.airHumidity.avg.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Min Humidity</p>
                      <p className="font-medium">{stats.airHumidity.min.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max Humidity</p>
                      <p className="font-medium">{stats.airHumidity.max.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center">No data available</p>
          )}
        </CardContent>
      </Card>

      {/* Battery Voltage Chart */}
      {chartData.some(d => d.battery > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Battery Voltage</CardTitle>
            <CardDescription>
              Device battery voltage over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Voltage (V)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={6}
                />
                <Tooltip
                  formatter={(value: number) => `${value.toFixed(2)}V`}
                />
                <Line
                  type="monotone"
                  dataKey="battery"
                  stroke="#f59e0b"
                  name="Battery"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  )
}